import React, { Fragment } from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import { graphql } from 'react-apollo';
import { flowRight as compose } from 'lodash';
import { intlShape, injectIntl } from 'react-intl';
import withAuth, { ORG_PERMISSIONS, withAuthPropType } from '../../../../hocs/withAuth';
import WarningModal from '../../WarningModal';
import {
  MUTATION_ADD_PROPERTY_TO_ORGANIZATION_FAVOURITES,
  MUTATION_REMOVE_PROPERTY_FROM_ORGANIZATION_FAVOURITES
} from '../../../../gql/Map/gql';

import PropertyListItem from './PropertyListItem';

import * as lodgelink from '../../../../constants/messages/lodgeLink';
import Modal from '../../../BaseModal';
import PropertyItemsContainer from '../../../PropertyItemsContainer/PropertyItemsContainer';
import PropertyAvailability from '../../../../constants/enums/PropertyAvailability';

import { PreferredPropertiesContainer, RelevantPropertiesContainer } from './styles';

class PropertyItemsAssembly extends React.Component {
  static propTypes = {
    auth: withAuthPropType.isRequired,
    intl: intlShape.isRequired,
    properties: PropTypes.arrayOf(PropTypes.shape({})),
    favouriteProperties: PropTypes.arrayOf(PropTypes.shape({})),
    currentActive: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    startingAtLabel: PropTypes.string.isRequired,
    priceFrequencyLabel: PropTypes.string.isRequired,
    callForPricingLabel: PropTypes.string.isRequired,
    openOnNew: PropTypes.func.isRequired,
    analytics: PropTypes.shape({
      trackEvent: PropTypes.func
    }).isRequired
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { properties, favouriteProperties: newFavouriteProperties } = nextProps;
    let { favouriteProperties, previousFavouriteProperties } = prevState;

    const isFavouritesUpdated = !isEqual(newFavouriteProperties, previousFavouriteProperties);

    let updatedFavouriteProperties = [];

    if (isFavouritesUpdated) {
      updatedFavouriteProperties = [...newFavouriteProperties];
      favouriteProperties = [...newFavouriteProperties];
      previousFavouriteProperties = [...newFavouriteProperties];
    } else {
      updatedFavouriteProperties = [...favouriteProperties];
    }

    const favouritePropertiesId = updatedFavouriteProperties.map(property => property.id);

    properties.forEach(property => {
      if (favouritePropertiesId.includes(parseInt(property.id))) {
        property.favourite = true;
      }
    });

    const preferredProperties = properties.filter(property => property.favourite);
    const relevantProperties = properties.filter(property => !property.favourite);

    return { preferredProperties, relevantProperties, favouriteProperties, previousFavouriteProperties };
  }

  static defaultProps = {
    properties: [],
    favouriteProperties: [],
    currentActive: undefined
  };

  constructor(props) {
    super(props);
    const { favouriteProperties, favouriteProperties: previousFavouriteProperties } = props;

    this.handlePropertyClick = this.handlePropertyClick.bind(this);
    this.closeWarningModal = this.closeWarningModal.bind(this);
    this.checkCallForPricing = this.checkCallForPricing.bind(this);

    this.state = {
      preferredProperties: [],
      relevantProperties: [],
      showWarningModal: false,
      selectedPropertySlug: null,
      property: null,
      favouriteProperties,
      previousFavouriteProperties
    };
  }

  handlePropertyClick = property => {
    const { openOnNew, selectedBypassReason, disconnectedProperty } = this.props;
    const { preferredProperties } = this.state;

    // Need to disable property click if CX has checked a connectedBypass option but not selected a connectedBypassReason
    if (disconnectedProperty && selectedBypassReason === 0) {
      return;
    }
    // If property is not set as favorite and there are preferred properties set for organization then show a modal
    // otherwise, just open the clicked property in the new window
    if (!property.favourite && preferredProperties.length) {
      this.setState({
        showWarningModal: true,
        selectedPropertySlug: property.slug,
        property
      });
    } else {
      openOnNew(property.slug, property);
    }
  };

  handleUpdateFavourite = updatedProperty => {
    const { preferredProperties, relevantProperties, favouriteProperties } = this.state;
    const { addPropertyToOrganizationFavourites, removePropertyFromOrganizationFavourites } = this.props;

    [preferredProperties, relevantProperties].map(properties =>
      properties.forEach(property => {
        if (updatedProperty.id === property.id) {
          const params = {
            variables: { propertyId: parseInt(property.id) }
          };

          let updatedFavouriteProperties = [];

          if (property.favourite) {
            removePropertyFromOrganizationFavourites(params);
            updatedFavouriteProperties = favouriteProperties.filter(
              favouriteProperty => favouriteProperty.id !== parseInt(property.id)
            );
          } else {
            addPropertyToOrganizationFavourites(params);
            updatedFavouriteProperties = [
              ...favouriteProperties,
              { id: parseInt(property.id), __typename: 'Property' }
            ];
          }

          property.favourite = !property.favourite;

          this.setState({
            favouriteProperties: [...updatedFavouriteProperties]
          });
        }
      })
    );

    this.setState({ preferredProperties, relevantProperties });
  };

  checkCallForPricing = property => {
    const { intl, auth } = this.props;

    if (auth?.me?.admin) return null;

    if (
      property.callForPricing ||
      (!property.callForPricing && (property.price === undefined || property.price === null))
    ) {
      return intl.formatMessage(lodgelink.default.phoneNumber);
    }

    return null;
  };

  closeWarningModal = (openSelected = false) => {
    const { openOnNew } = this.props;
    const { selectedPropertySlug, property } = this.state;

    if (openSelected && selectedPropertySlug) {
      openOnNew(selectedPropertySlug, property);
    }

    this.setState({ showWarningModal: false });
  };

  renderPreferredProperties() {
    const { preferredProperties } = this.state;

    if (preferredProperties.length) {
      return (
        <PreferredPropertiesContainer>{this.renderPropertiesList(preferredProperties)}</PreferredPropertiesContainer>
      );
    }

    return null;
  }

  renderRelevantProperties() {
    const { relevantProperties } = this.state;

    if (relevantProperties.length) {
      return <RelevantPropertiesContainer>{this.renderPropertiesList(relevantProperties)}</RelevantPropertiesContainer>;
    }

    return null;
  }

  renderPropertiesList(properties) {
    const {
      currentActive,
      startingAtLabel,
      priceFrequencyLabel,
      callForPricingLabel,
      callForAvailability,
      auth,
      handlePropertyPaneOpen
    } = this.props;

    const permissions = get(auth, 'me.activeOrganization.permissions', []);
    const canEditOrganizationDetails = permissions.includes(ORG_PERMISSIONS.EDIT_ORGANIZATION);

    return properties.map(property => {
      if (property.id === currentActive) {
        property.active = true;
      } else {
        property.active = false;
      }
      property.callForPricing = this.checkCallForPricing(property);

      return (
        <Fragment key={property.id}>
          <span id={`property-${property.id}`} />
          <PropertyListItem
            model={property}
            showFavourite={canEditOrganizationDetails}
            onClick={() => {
              handlePropertyPaneOpen(property);
            }}
            startingAtLabel={startingAtLabel}
            priceFrequencyLabel={priceFrequencyLabel}
            callForPricingLabel={
              property.availability === PropertyAvailability.SOLD_OUT ? callForAvailability : callForPricingLabel
            }
            updateFavourite={e => {
              e.stopPropagation();
              this.handleUpdateFavourite(property);
            }}
          />
        </Fragment>
      );
    });
  }

  render() {
    const { intl } = this.props;
    const { showWarningModal } = this.state;
    const screenClass = 'lg';

    return (
      <>
        <PropertyItemsContainer key="property-item-container">
          {this.renderPreferredProperties()}
          {this.renderRelevantProperties()}
        </PropertyItemsContainer>
        <Modal
          handleClose={() => {
            this.closeWarningModal();
          }}
          height={['xs', 'sm', 'md'].includes(screenClass) ? '100%' : 'auto'}
          width={['xs', 'sm', 'md'].includes(screenClass) ? '100%' : '767px'}
          open={showWarningModal}
        >
          <WarningModal
            screenClass={screenClass}
            intl={intl}
            onContinueHook={() => {
              this.closeWarningModal(true);
            }}
            onCloseHook={() => {
              this.closeWarningModal();
            }}
          />
        </Modal>
      </>
    );
  }
}

export default compose(
  injectIntl,
  withAuth({
    requireAuthentication: true
  }),
  graphql(MUTATION_ADD_PROPERTY_TO_ORGANIZATION_FAVOURITES, {
    name: 'addPropertyToOrganizationFavourites',
    options: () => ({
      errorPolicy: 'all'
    })
  }),
  graphql(MUTATION_REMOVE_PROPERTY_FROM_ORGANIZATION_FAVOURITES, {
    name: 'removePropertyFromOrganizationFavourites',
    options: () => ({
      errorPolicy: 'all'
    })
  })
)(PropertyItemsAssembly);
